<template>
    <div>
        <div class="flex items-center justify-between">
            <div>
                <h2 class="mb-0">
                    Resource Types
                </h2>
                <p class="mb-0">
                    {{ subjectArea.name }}
                </p>
            </div>
            <div>
                <div class="flex items-center">
                    <d-dropdown trap-focus>
                        <d-btn slot="activator" flat class="mr-3">
                            <d-icon icon="filter" class="pt-1" />
                        </d-btn>
                        <d-dropdown-item :focusable="false" custom paddingless>
                            <div style="width:250px">
                                <d-field label="Search by Name">
                                    <d-input v-model="filter" />
                                </d-field>
                            </div>
                        </d-dropdown-item>
                    </d-dropdown>
                    <d-btn color="secondary-500" @click="openResourceTypeModal()">
                        + Add Resource Type
                    </d-btn>
                </div>
            </div>
        </div>
        <d-table 
            class="bg-white shadow mt-10 mb-5" 
            :data="resourceTypes" 
            :columns="tableColumns"
            :loading="loading" 
            backend-search 
            backend-sorting 
            :default-sort="[pagination.sortField, pagination.sortOrder]" 
            striped 
            default-sort-direction="desc" 
            @sort="onSort">
            <template #default="{ row }">
                <td>
                    <d-icon :icon="row.imageUrl" pack="fad" class="ml-2 text-xl text-green" />
                </td>
                <td>{{ row.name }}</td>
                <td>
                    <d-icon icon="edit" class="cursor-pointer ml-2 text-lg" @click="openResourceTypeModal(row)" />
                </td>
                <td>
                    <d-icon icon="trash" class="cursor-pointer text-error ml-3 text-lg" @click="showDeletePrompt(row)" />
                </td>
            </template>
        </d-table>
        <div class="flex justify-end">
            <d-pagination :total-items="total" :per-page="pagination.pageSize" v-model:current="pagination.page" />
        </div>

        <!-- Resource Type Modal -->
        <resource-type-modal
            :active="resourceTypeModalActive"
            :resource-type="selectedResourceType"
            :subject-area="subjectArea"
            @close="closeResourceTypeModal"
            @saved="onResourceTypeSaved"
        />
    </div>
</template>

<script>
  import axios from 'axios';
  import debounce from 'lodash/debounce';
  import { showErrorToast } from '@services/errorHandling.service';
  import ResourceTypeModal from './components/ResourceTypeModal.vue';

  export default {
    parent: true,
    
    components: {
      ResourceTypeModal,
    },
    
    props: {
      subjectArea: {
        type: Object,
        default: () => ({}),
      },
    },
    data: () => ({
      resourceTypes: [],
      loading: false,
      filter: '',
      total: 0,
      resourceTypeModalActive: false,
      selectedResourceType: null,
      pagination: {
        page: 1,
        pageSize: 20,
        sortField: 'name',
        sortOrder: 'asc',
      },
      tableColumns: [
        { field: 'imageUrl', label: 'Icon', sortable: false },
        { field: 'name', label: 'Name', sortable: true },
        { field: 'edit', label: 'Edit', sortable: false },
        { field: 'delete', label: 'Delete', sortable: false }
      ]
    }),

    watch: {
      pagination: {
        handler() {
          this.getResourceTypesPaged();
        },
        deep: true,
      },

      filter() {
        this.getDebouncedResourceTypesPaged();
      },
    },

    mounted() {
      this.getResourceTypesPaged();
    },

    methods: {
      async getResourceTypesPaged() {
        const {
          page, sortField, sortOrder, pageSize,
        } = this.pagination;
        const params = [`filter=${this.filter}`, `sortDir=${sortField} ${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`].join('&');
        this.loading = true;
        const { data } = await axios.get(`/api/subject-areas/${this.subjectArea.id}/resource-types/paged?${params}`);
        this.resourceTypes = data.resultItems;
        this.total = data.totalRecords;
        this.loading = false;
      },

      getDebouncedResourceTypesPaged: debounce(function () {
        this.getResourceTypesPaged();
      }, 500),

      openResourceTypeModal(resourceType = null) {
        this.selectedResourceType = resourceType;
        this.resourceTypeModalActive = true;
      },

      closeResourceTypeModal() {
        this.resourceTypeModalActive = false;
        this.selectedResourceType = null;
      },

      onResourceTypeSaved() {
        this.getResourceTypesPaged();
      },

      async showDeletePrompt(resourceType) {
        try {
          await this.$dialog.confirm({
            title: 'Deleting Resource Type',
            message: `Are you sure you want to delete <b>${resourceType.name}</b>? This action cannot be undone.`,
            confirmText: 'Delete Resource Type',
            confirmColor: 'error',
          });
        } catch {
          // User cancelled the dialog — nothing to do
          return;
        }

        try {
          await axios.delete(`/api/subject-areas/resource-types/${resourceType.id}`);
          await this.getResourceTypesPaged();
          this.$toast.success('Resource type was deleted');
        } catch (error) {
          showErrorToast(error);
        }
      },

      onSort(field, order) {
        this.pagination.sortField = field;
        this.pagination.sortOrder = order;
      },
    },
  };
</script>